import { useCallback, useRef } from "react";

import { offset } from "@floating-ui/dom";
import { faCheck, faEye } from "@fortawesome/pro-regular-svg-icons";

import { useFloatingWindowsActions } from "src/base-components/FloatingWindow/hooks";
import { Icon } from "src/base-components/Icon";
import { Pill } from "src/base-components/Pill";
import { HistorySingleEntry } from "src/cases/caseView/history/HistoryItemFrame";
import { useGetCaseTemplateTask } from "src/cases/caseView/hooks";
import { useCase } from "src/cases/queries/cases";
import { CaseHistoryEvent } from "src/cases/queries/casesTypes";
import { Tooltip } from "src/design-system/Tooltip";
import { CasePageParams } from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

export const TaskCompleted: React.FC<{
  entry: CaseHistoryEvent<"task_completed">;
  isLast: boolean;
}> = ({ entry, isLast }) => {
  const { case_id: caseId } = useParamsDecode<CasePageParams>();
  const { data: caseData } = useCase(caseId);
  const getCaseTemplateTask = useGetCaseTemplateTask(caseData);
  const { open } = useFloatingWindowsActions();
  const buttonRef = useRef<HTMLDivElement>(null);

  const { task_key, task_form_data } = entry.event_data;
  const templateTask = getCaseTemplateTask(task_key);
  const taskDisplayName = templateTask?.display_name || task_key;

  const hasFormFields =
    templateTask?.form?.fields && templateTask.form.fields.length > 0;
  const hasFormData = task_form_data && Object.keys(task_form_data).length > 0;
  const shouldShowForm = hasFormFields && hasFormData;

  const handleOpenForm = useCallback(() => {
    open(
      {
        id: `history-task-form-${entry.id}`,
        type: "cm-history",
        target: buttonRef,
        positionOptions: {
          placement: "left-start",
          middleware: [offset({ mainAxis: 60, crossAxis: 0 })],
        },
      },
      {
        formType: "task",
        key: task_key,
        displayName: taskDisplayName,
        formData: task_form_data,
        caseId,
        description: templateTask?.description,
      },
    );
  }, [
    open,
    entry.id,
    task_key,
    taskDisplayName,
    task_form_data,
    caseId,
    templateTask?.description,
  ]);

  return (
    <HistorySingleEntry
      actorId={entry.user_id}
      eventType={entry.event_type}
      headline={
        <>
          <Pill size="sm" variant="gray">
            <Pill.Text>{taskDisplayName}</Pill.Text>
          </Pill>{" "}
          task was marked as complete.
        </>
      }
      icon={faCheck}
      isLast={isLast}
      sideAction={
        shouldShowForm ? (
          <div ref={buttonRef}>
            <Tooltip delay={250} placement="top-end" title="View form" asChild>
              <Icon
                color="text-gray-500 hover:text-gray-700"
                dataLoc={`task-view-form-${task_key}`}
                icon={faEye}
                size="sm"
                onClick={handleOpenForm}
              />
            </Tooltip>
          </div>
        ) : undefined
      }
      timestamp={entry.timestamp}
    />
  );
};
